import {
  BILLING_METRIC_SOURCES,
  DISPATCH_OFFER_KINDS,
  DISPATCH_SEARCH_STATES,
  DISPATCH_STRATEGIES,
  missedServicesColumns,
  missedServicesDefaultColumns,
  missedServicesFilters,
  missedServicesSorts,
  missedServicesSummaryFields,
  OFFER_STATUSES,
  PAYMENT_STATUSES,
  REPORT_PAYMENT_STATES,
  SERVICE_MODES,
} from '@cabgo/shared';
import type { ReportViewDefinition } from '../reportViewTypes';

const enumFields: Record<string, readonly string[]> = {
  date_basis: ['TERMINAL_FAILURE_EVENT', 'DISPATCH_FAILED_AT', 'UNAVAILABLE'],
  service_date_basis: ['SCHEDULED_START', 'CREATED_AT_FALLBACK'],
  scope_provenance: ['CURRENT_SERVICE_GROUP'],
  search_association: ['LINKED_SEARCH', 'TEMPORAL_ASSOCIATION', 'LATEST_AVAILABLE', 'UNAVAILABLE'],
  dispatch_evidence_state: [
    'LINKED_SEARCH',
    'TEMPORAL_ASSOCIATION',
    'LATEST_AVAILABLE',
    'EXPIRED',
    'UNAVAILABLE',
  ],
  latest_search_mode: ['AUTO', 'MANUAL'],
  dispatch_mode: ['AUTO', 'MANUAL'],
  latest_search_strategy: DISPATCH_STRATEGIES,
  latest_search_state: DISPATCH_SEARCH_STATES,
  latest_offer_status: OFFER_STATUSES,
  latest_offer_kind: DISPATCH_OFFER_KINDS,
  failure_actor_type: ['SYSTEM', 'PASSENGER', 'AGENT', 'ORG_USER'],
  fare_state: ['NONE', 'PROVISIONAL', 'FINAL', 'CANCELLED_SNAPSHOT'],
  quote_provenance: ['BOOKING_SNAPSHOT', 'UNAVAILABLE'],
  payment_state: REPORT_PAYMENT_STATES,
  base_payment_status: PAYMENT_STATUSES,
  overage_payment_status: PAYMENT_STATUSES,
  service_mode: SERVICE_MODES,
  trip_status: ['MISSED'],
  trip_leg: ['OUTBOUND', 'RETURN'],
  billing_distance_source: BILLING_METRIC_SOURCES,
  billing_duration_source: BILLING_METRIC_SOURCES,
};
const valueLabels = Object.fromEntries(
  Object.entries(enumFields).map(([field, values]) => [
    field,
    Object.fromEntries(values.map((value) => [value, `missedServices_value_${value}`])),
  ]),
);
valueLabels.latest_offer_status!.EXPIRED = 'missedServices_value_EXPIRED_OFFER';
export const missedServicesView: ReportViewDefinition = {
  key: 'missed_services',
  titleKey: 'missedServices_title',
  descriptionKey: 'missedServices_description',
  dateBasisKey: 'missedServices_dateBasis',
  columns: missedServicesColumns,
  defaultColumns: missedServicesDefaultColumns,
  dateMode: 'range',
  supportsSearch: true,
  defaultSort: 'failedAt',
  sorts: missedServicesSorts,
  filters: missedServicesFilters.map((filter) => ({
    ...filter,
    labelKey: `missedServices_filter_${filter.id}`,
    ...(filter.values
      ? {
          options: filter.values.map((value) => ({
            value,
            labelKey: `missedServices_value_${filter.id === 'latestOfferState' && value === 'EXPIRED' ? 'EXPIRED_OFFER' : value}`,
          })),
        }
      : {}),
  })),
  valueLabels,
  summary: missedServicesSummaryFields
    .filter((field) => field !== 'missingFailureDateCount')
    .map((field) => ({
      field,
      labelKey: `missedServices_summary_${field}`,
      unit: field.endsWith('Sum') ? 'money' : 'count',
    })),
  primarySummary: [
    'missedTripCount',
    'fareTotalSum',
    'billedTotalSum',
    'receivedTotalSum',
    'outstandingTotalSum',
  ],
  summaryGroups: [
    { labelKey: 'summaryGroupOverview', fields: ['missedTripCount'] },
    {
      labelKey: 'summaryGroupFinance',
      fields: [
        'fareTotalSum',
        'billedTotalSum',
        'receivedTotalSum',
        'outstandingTotalSum',
        'freeTripCount',
        'financeReviewCount',
      ],
    },
    {
      labelKey: 'summaryGroupEvidence',
      fields: [
        'missingFareCount',
        'missingEventCount',
        'missingLinkedSearchCount',
        'expiredEvidenceCount',
        'failureDateMismatchCount',
        'unknownReasonCount',
      ],
    },
  ],
  summaryBreakdowns: [
    {
      prefix: 'failureReason:',
      labelKey: 'missedServices_reasonBreakdown',
      valueLabels: { UNKNOWN: 'missedServices_value_UNKNOWN' },
    },
  ],
  coverage: [{ field: 'missingFailureDate', labelKey: 'missedServices_coverage_missingFailureDate' }],
};
